import { Crown } from "lucide-react";

/** Gold "Premium" chip shown next to a member's name across the app. */
export function PremiumBadge({
  label = "Premium",
  size = "sm",
  className = "",
}: {
  label?: string;
  size?: "xs" | "sm" | "md";
  className?: string;
}) {
  const scale =
    size === "xs"
      ? "px-1.5 py-0.5 text-[9px] gap-0.5"
      : size === "md"
        ? "px-3 py-1 text-xs gap-1.5"
        : "px-2 py-0.5 text-[10px] gap-1";
  const icon = size === "md" ? "size-3.5" : "size-3";

  return (
    <span
      className={`inline-flex items-center rounded-full border border-neon-yellow/60 bg-gradient-to-r from-neon-yellow/30 via-neon-yellow/15 to-neon-pink/25 font-bold uppercase tracking-wide text-ink shadow-sm ${scale} ${className}`}
      title="Inquira Premium member"
    >
      <Crown className={`${icon} text-neon-yellow`} aria-hidden="true" />
      {label}
    </span>
  );
}

/**
 * Gold profile border for Premium members. Falls back to the normal rule
 * border for free accounts so the ring stays a real status signal.
 */
export function PremiumRing({
  isPremium,
  children,
  className = "",
}: {
  isPremium: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full p-[2px] ${
        isPremium
          ? "bg-[conic-gradient(from_180deg,var(--color-neon-yellow),var(--color-neon-pink),var(--color-neon-yellow))] shadow-md"
          : "bg-rule"
      } ${className}`}
    >
      <span className="flex items-center justify-center rounded-full bg-card">{children}</span>
    </span>
  );
}

/** Small avatar with the Premium gold border applied. */
export function PremiumAvatar({
  isPremium,
  label,
  avatarUrl,
  size = 36,
}: {
  isPremium: boolean;
  label: string;
  avatarUrl?: string | null;
  size?: number;
}) {
  const initials = label.trim().slice(0, 1).toUpperCase() || "?";
  return (
    <PremiumRing isPremium={isPremium}>
      <span
        className="flex items-center justify-center overflow-hidden rounded-full bg-muted text-xs font-extrabold text-ink"
        style={{ width: size, height: size }}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="size-full object-cover" />
        ) : (
          initials
        )}
      </span>
    </PremiumRing>
  );
}